import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Outlet, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { AccountReminder } from "./AccountReminder";
import { describeSignIn, signedInWith } from "../auth/signedInWith";
import { useOwnAvatar } from "../modules/account/useOwnAvatar";
import { roleLabels } from "../api/operatorLogins";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import ListSubheader from "@mui/material/ListSubheader";
import { useAuth } from "react-oidc-context";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutlined";
import BuildIcon from "@mui/icons-material/BuildOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import SmsIcon from "@mui/icons-material/SmsOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import RouterIcon from "@mui/icons-material/RouterOutlined";
import BarChartIcon from "@mui/icons-material/BarChartOutlined";
import DnsIcon from "@mui/icons-material/DnsOutlined";
import PaletteIcon from "@mui/icons-material/PaletteOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import GridViewIcon from "@mui/icons-material/GridViewOutlined";
import HubIcon from "@mui/icons-material/HubOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useState, type MouseEvent, type ReactElement } from "react";
import { MenuSearch } from "./MenuSearch";
import { BrandLockup } from "./Brand";
import { ColorModeToggle } from "./ColorModeToggle";
import { VersionLine } from "./VersionLine";
import { useIdentity } from "../auth/useIdentity";
import { flagEnabled } from "../config";
import { accountMenu, groupOf, leafIsHere, type NavGroup, type NavGroupKey, type NavLeaf } from "./nav";
import { shortName } from "../auth/scope";
import { useProviders } from "../modules/providers/useProviders";
import { useNavGroups } from "./useNavGroups";

/**
 * The signed-in frame: header, collapsible grouped sidebar, page.
 *
 * The information architecture is the one the owner approved on the Welcome
 * Page canvas (2026-09-14):
 *   - the logo is the way home; there is no "Dashboard" entry and no such word
 *   - the sidebar is groups with submenus, and folds to icons with tooltips
 *   - the operator's own controls (profile, sign out) live behind the avatar
 *     on the right, with the first name and the namespace at the top of it
 *   - below md the sidebar is a slide-over drawer, always expanded
 */
const WIDTH = 240;
const RAIL = 64;
const COLLAPSED_KEY = "ankhos.nav.collapsed";

const ICONS: Record<NavGroupKey, ReactElement> = {
  platform: <HubIcon />,
  providers: <GridViewIcon />,
  plans: <LayersIcon />,
  promotions: <LocalOfferIcon />,
  users: <PeopleIcon />,
  payments: <PaymentsIcon />,
  sms: <SmsIcon />,
  tools: <BuildIcon />,
  billing: <ReceiptIcon />,
  devices: <RouterIcon />,
  reports: <BarChartIcon />,
  provisioning: <DnsIcon />,
  themes: <PaletteIcon />,
  roster: <BadgeIcon />,
  legal: <GavelIcon />,
  settings: <SettingsIcon />,
};

/** A leaf is a router link, or a plain link to the PHP portal in a new tab. */
function leafLink(i: NavLeaf) {
  return i.external
    ? ({ component: "a", href: i.to, target: "_blank", rel: "noopener", title: "Opens the PHP portal in a new tab" } as const)
    : ({ component: RouterLink, to: i.to } as const);
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}
function writeCollapsed(v: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, v ? "1" : "0");
  } catch {
    /* a private window keeps the default */
  }
}

/** The expanded sidebar: every group a row, its items a submenu underneath. */
function GroupList({ groups, pathname, onLeaf, onSwitch }: { groups: NavGroup[]; pathname: string; onLeaf: () => void; onSwitch: (ns: string) => void }) {
  // The group the current page belongs to starts open; every other group,
  // Reports included, starts closed (owner 2026-10-22).
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const here = groupOf(groups, pathname);
    return here ? { [here]: true } : {};
  });
  return (
    <List component="nav" aria-label="Main" sx={{ py: 1 }}>
      {groups.map((g) => {
        const isOpen = open[g.key] ?? false;
        const active = g.items.some((i) => leafIsHere(i, pathname));
        return (
          <Box key={g.key}>
            <ListItemButton
              onClick={() => setOpen((o) => ({ ...o, [g.key]: !isOpen }))}
              aria-expanded={isOpen}
              sx={{ minHeight: 40, fontWeight: active ? 600 : 500 }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>{ICONS[g.key]}</ListItemIcon>
              <ListItemText primary={leafLabel({ label: g.label, ai: g.items.some((i) => i.ai) })} slotProps={{ primary: { sx: { fontWeight: "inherit", fontSize: 14 } } }} />
              {isOpen ? <ExpandLessIcon fontSize="small" sx={{ color: "text.disabled" }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: "text.disabled" }} />}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding onClick={onLeaf}>
                {g.items.map((i) =>
                  i.disabled ? (
                    <Box key={i.to} title="Not available yet">
                      <ListItemButton disabled aria-disabled="true" sx={{ minHeight: 32, pl: 5.5, py: 0.25 }}>
                        <ListItemText primary={leafLabel(i)} slotProps={{ primary: { sx: { fontSize: 13, fontWeight: 500 } } }} />
                      </ListItemButton>
                    </Box>
                  ) : (
                    <ListItemButton
                      key={i.to}
                      {...leafLink(i)}
                    selected={leafIsHere(i, pathname)}
                    onClick={i.switchTo ? (e: MouseEvent) => { e.preventDefault(); onSwitch(i.switchTo!); } : undefined}
                    sx={{ minHeight: 32, pl: 5.5, py: 0.25 }}
                  >
                    <ListItemText primary={leafLabel(i)} slotProps={{ primary: { sx: { fontSize: 13, fontWeight: leafIsHere(i, pathname) ? 600 : 500 } } }} />
                      {i.external && <OpenInNewIcon sx={{ fontSize: 14, color: "text.disabled" }} />}
                    </ListItemButton>
                  ),
                )}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </List>
  );
}

/** The folded sidebar: one icon per group, its name in a tooltip, its items in a menu. */
function IconRail({ groups, pathname, onSwitch }: { groups: NavGroup[]; pathname: string; onSwitch: (ns: string) => void }) {
  const navigate = useNavigate();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; group: NavGroup } | null>(null);
  return (
    <List component="nav" aria-label="Main" sx={{ py: 1 }}>
      {groups.map((g) => {
        const active = g.items.some((i) => leafIsHere(i, pathname));
        return (
          <Tooltip key={g.key} title={leafLabel({ label: g.label, ai: g.items.some((i) => i.ai) })} placement="right">
            <ListItemButton
              aria-label={g.label}
              aria-haspopup={g.items.length > 1 ? "menu" : undefined}
              selected={active}
              onClick={(e: MouseEvent<HTMLElement>) => {
                const only = g.items.length === 1 ? g.items[0]! : null;
                if (only?.disabled) return;
                if (only?.switchTo) onSwitch(only.switchTo);
                else if (only && !only.external) navigate(only.to);
                else if (only) window.open(only.to, "_blank", "noopener");
                else setMenu({ anchor: e.currentTarget, group: g });
              }}
              sx={{ minHeight: 40, justifyContent: "center", px: 0, mx: 1.25, borderRadius: "6px" }}
            >
              <ListItemIcon sx={{ minWidth: 0, color: "inherit" }}>{ICONS[g.key]}</ListItemIcon>
            </ListItemButton>
          </Tooltip>
        );
      })}
      <Menu
        open={menu !== null}
        anchorEl={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menu?.group.items.map((i) =>
          i.disabled ? (
            <MenuItem key={i.to} disabled title="Not available yet">
              {i.label}
            </MenuItem>
          ) : (
          <MenuItem
            key={i.to}
            {...leafLink(i)}
            selected={leafIsHere(i, pathname)}
            onClick={(e: MouseEvent) => {
              setMenu(null);
              if (i.switchTo) {
                e.preventDefault();
                onSwitch(i.switchTo);
              }
            }}
          >
            {i.label}
            {i.external && <OpenInNewIcon sx={{ fontSize: 14, color: "text.disabled", ml: 1 }} />}
          </MenuItem>
          ),
        )}
      </Menu>
    </List>
  );
}

/**
 * The hybrid context switch the owner chose (2026-09-14): the sidebar lists
 * providers for discovery, and THIS control -- always in the header -- shows
 * where you are and switches from anywhere. Name, then namespace, then the
 * list with a filter for the 20–100+ case.
 */
function ContextControl({ onSwitch }: { onSwitch: (ns: string | null) => void }) {
  const identity = useIdentity();
  const { all, inside } = useProviders();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState("");
  const root = identity.rootNamespace;
  if (!root) return null;
  // An applicant's namespace is a temporary id, not a name to show anyone.
  const applicantScope = root.startsWith("applicant.");
  const rootName = applicantScope ? "Setup in progress" : shortName(root);
  const needle = q.trim().toLowerCase();
  const hits = all.filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.namespace.toLowerCase().includes(needle));
  const name = inside ? inside.name : rootName;
  const sub = inside ? `provider of ${rootName}` : all.length ? `parent · ${all.length} provider${all.length === 1 ? "" : "s"}` : "";
  const choose = (ns: string | null) => {
    setAnchor(null);
    setQ("");
    onSwitch(ns);
  };
  return (
    <>
      <Button
        aria-label="Switch operator context"
        aria-haspopup="menu"
        onClick={(e) => setAnchor(e.currentTarget)}
        variant="outlined"
        color="inherit"
        size="small"
        endIcon={all.length ? <UnfoldMoreIcon fontSize="small" /> : undefined}
        disabled={!all.length && !inside}
        sx={{ textTransform: "none", textAlign: "left", borderColor: "divider", px: 1.25, py: 0.25, lineHeight: 1.15, display: { xs: "none", sm: "inline-flex" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }} data-testid="context-name">
            {name}
            {sub && (
              <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                · {sub}
              </Typography>
            )}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }} data-testid="context-namespace">
            {applicantScope ? "" : identity.namespace}
          </Typography>
        </Box>
      </Button>
      <Menu open={anchor !== null} anchorEl={anchor} onClose={() => choose(inside ? inside.namespace : null)} slotProps={{ paper: { sx: { width: 360 } } }}>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <InputBase
            autoFocus
            fullWidth
            placeholder="Search providers"
            inputProps={{ "aria-label": "Search providers" }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ border: 1, borderColor: "divider", borderRadius: "6px", px: 1.25, py: 0.5, fontSize: 14 }}
          />
        </Box>
        <MenuItem selected={!inside} onClick={() => choose(null)}>
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {rootName}{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                · parent{!inside ? ", you are here" : ""}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
              {root}
            </Typography>
          </Box>
          {!inside && <CheckIcon fontSize="small" />}
        </MenuItem>
        {all.length > 0 && <ListSubheader disableSticky sx={{ lineHeight: "32px" }}>{needle ? `${hits.length} of ${all.length} providers match` : `${all.length} providers`}</ListSubheader>}
        {hits.map((p) => (
          <MenuItem key={p.namespace} selected={inside?.namespace === p.namespace} onClick={() => choose(p.namespace)}>
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {p.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                {p.namespace}
              </Typography>
            </Box>
            {inside?.namespace === p.namespace ? <CheckIcon fontSize="small" /> : <Typography variant="caption" color="text.disabled">Open console</Typography>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Says, in words, that you are inside a provider -- never colour alone (the
 * brief) -- and offers the way back. Every action on the page applies to the
 * provider named here.
 */
function ContextBar({ onBack }: { onBack: () => void }) {
  const identity = useIdentity();
  const { inside } = useProviders();
  if (!identity.provider || !identity.rootNamespace) return null;
  const rootName = shortName(identity.rootNamespace);
  return (
    <Box
      role="status"
      data-testid="context-bar"
      sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: 2, py: 0.75, mb: 2, bgcolor: "brand.surface2", border: 1, borderColor: "divider", borderRadius: "8px", fontSize: 13 }}
    >
      <GridViewIcon fontSize="small" />
      <Typography variant="body2">
        You are operating inside the provider <strong>{inside?.name ?? identity.provider}</strong> (<code>{identity.provider}</code>). Every action here applies to{" "}
        {inside?.name ?? identity.provider} only.
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: "brand.link" }}>
        Back to {rootName}
      </Button>
    </Box>
  );
}

function AccountMenu() {
  const identity = useIdentity();
  const auth = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const items = accountMenu(flagEnabled, identity.roles);
  const initials = identity.firstName ? identity.firstName.slice(0, 1).toUpperCase() : null;
  const via = signedInWith();
  const photo = useOwnAvatar();
  return (
    <>
      <Tooltip title={identity.username ? `Signed in as ${identity.username}` : "Account"}>
        <IconButton aria-label="Account menu" onClick={(e) => setAnchor(e.currentTarget)} size="large" sx={{ p: 0.5 }}>
          {/* Their photo (Settings › Sign-in & security › Photo); else initials, else a person icon. */}
          <Avatar src={photo ?? undefined} alt={identity.firstName ?? "Account"} sx={{ width: 32, height: 32, bgcolor: "primary.main", color: "primary.contrastText", fontSize: 14, fontWeight: 700 }}>
            {initials ?? <PersonIcon fontSize="small" />}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu open={anchor !== null} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        {/* First item: who you are, where you are. First name first (the brief); the username under it (owner 2026-10-25). */}
        <Box sx={{ px: 2, py: 1, minWidth: 220 }} data-testid="account-identity">
          <Typography variant="subtitle2">{identity.firstName ?? "Operator"}</Typography>
          {/* Owner 2026-10-25: which login am I? */}
          {identity.username && (
            <Typography variant="caption" component="div" data-testid="account-username">
              Signed in as <strong>{identity.username}</strong>
            </Typography>
          )}
          {/* Owner 2026-10-25: also the way in this time (mobile, email, Google). */}
          {via && (
            <Typography variant="caption" component="div" color="text.secondary" data-testid="account-signed-in-with">
              with {describeSignIn(via)}
            </Typography>
          )}
          <Typography variant="caption" sx={{ fontFamily: "monospace" }} color="text.secondary">
            {identity.namespace ?? "no namespace"}
          </Typography>
          <Typography variant="caption" component="div" color={identity.roles.length ? "text.secondary" : "error"}>
            {/* Owner 2026-10-25: role names people know (Owner, Admin, Support…), not codes. */}
            {identity.roles.length ? roleLabels([...new Set(identity.roles.map((r) => r.toLowerCase()))]) : "no role — reads will be refused"}
          </Typography>
        </Box>
        <Divider />
        {items.map((i) => (
          <MenuItem key={i.to} component={RouterLink} to={i.to} onClick={() => setAnchor(null)}>
            {i.label}
          </MenuItem>
        ))}
        <MenuItem onClick={() => void auth.signoutRedirect()}>Sign out</MenuItem>
      </Menu>
    </>
  );
}

/** A leaf's name, with the AI icon on a screen that really uses a model. */
function leafLabel(i: { label: string; ai?: boolean }) {
  if (!i.ai) return i.label;
  return (
    <>
      {i.label} <AutoAwesomeIcon aria-label="AI" titleAccess="AI" color="secondary" sx={{ fontSize: 14, verticalAlign: "text-bottom" }} />
    </>
  );
}

export function Shell() {
  const identity = useIdentity();
  const { pathname } = useLocation();
  const theme = useTheme();
  // Responsive: the sidebar is permanent from md (900px) up and a slide-over
  // below it, opened from the menu button. Same groups either way.
  const wide = useMediaQuery(theme.breakpoints.up("md"));
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const navigate = useNavigate();
  const groups = useNavGroups();
  // Entering a provider (or coming back) always lands on its Welcome Page:
  // the numbers on screen must belong to the namespace named in the header.
  const switchTo = (ns: string | null) => {
    identity.enter(ns);
    setOpen(false);
    navigate("/");
  };

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
  };
  const railWidth = collapsed ? RAIL : WIDTH;

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1 }}>
          {wide ? (
            <Tooltip title={collapsed ? "Expand navigation" : "Collapse navigation"}>
              <IconButton color="inherit" edge="start" aria-label={collapsed ? "Expand navigation" : "Collapse navigation"} onClick={toggleCollapsed}>
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton color="inherit" edge="start" aria-label="Open navigation" onClick={() => setOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          {/* 🔑 The logo is the way home. "/" rather than "/welcome": Landing
              redirects a signed-in visitor back to "/" at once, so /welcome
              would be a visible double hop to the same screen. */}
          <BrandLockup to="/" />

          {/* Owner 2026-10-22: search the menu, just before the namespace switch. */}
          <MenuSearch groups={groups} account={accountMenu(flagEnabled, identity.roles)} onSwitch={switchTo} />
          {/* Where you are: your own namespace, or the provider you entered. */}
          <ContextControl onSwitch={switchTo} />
          {!identity.namespace && (
            <Chip size="small" label="no namespace" color="error" sx={{ display: { xs: "none", sm: "inline-flex" } }} />
          )}
          <ColorModeToggle />
          <AccountMenu />
        </Toolbar>
      </AppBar>

      {wide ? (
        <Drawer
          variant="permanent"
          sx={{
            width: railWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: { width: railWidth, boxSizing: "border-box", overflowX: "hidden", transition: "width 150ms" },
          }}
        >
          <Toolbar />
          {collapsed ? <IconRail groups={groups} pathname={pathname} onSwitch={switchTo} /> : <GroupList groups={groups} pathname={pathname} onLeaf={() => undefined} onSwitch={switchTo} />}
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ [`& .MuiDrawer-paper`]: { width: WIDTH, boxSizing: "border-box" } }}
        >
          <Toolbar />
          <GroupList groups={groups} pathname={pathname} onLeaf={() => setOpen(false)} onSwitch={switchTo} />
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, minWidth: 0 }}>
        <Toolbar />
        <ContextBar onBack={() => switchTo(null)} />
        {/* Owner 2026-10-25: forced second-factor set-up, and a reminder to finish contact details. */}
        <AccountReminder />
        <Outlet />
        {/* Inside the signed-in shell too, not only on the public pages: an
            operator reporting a problem is reporting it from in here. */}
        <VersionLine />
      </Box>
    </Box>
  );
}
